import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Settings. The books themselves — addresses, GSTIN, ledgers — come from
 * Tally and are changed there; this page only holds what SyncBooks owns.
 */
export default function Settings() {
  const { role, setOrganization } = useAuth();
  const [org, setOrg] = useState(null);
  const [saving, setSaving] = useState(false);
  const readOnly = role !== "OWNER";

  useEffect(() => {
    api.get("/organization").then(({ data }) => setOrg(data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/organization", { name: org.name });
      setOrg(data);
      setOrganization(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(apiError(e));
    }
    setSaving(false);
  };

  if (!org) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="max-w-2xl space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-1 text-sm text-slate-500">
          Your books come from Tally and are changed there. The Tally link itself is under{" "}
          <Link to="/connect" className="text-blue-600 hover:underline">Tally connection</Link>.
        </p>
      </div>
      <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-600">Organisation name (shown in SyncBooks)</Label>
          <Input value={org.name || ""} disabled={readOnly} data-testid="org-name"
            onChange={(e) => setOrg((o) => ({ ...o, name: e.target.value }))} />
        </div>
        {!readOnly && (
          <Button onClick={save} disabled={saving || !org.name?.trim()} className="gap-1.5 bg-[#408dfb] hover:bg-[#2f7bea]">
            <Save size={15} /> Save
          </Button>
        )}
      </div>
    </div>
  );
}
